import React, { Fragment, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  HighlightOff as HighlightOffIcon,
  OpenInNew as OpenInNewIcon
} from '@material-ui/icons';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import ConfirmDialog from '../../../../common/dialogs/ConfirmDialog';
import {
  ErrorMetadataPlugin,
  ErrorMetadataPluginProps
} from '../../../../common/views/ErrorMetadataView';
import TaskStatusLabel from '../../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../../common/WrappedTableCell';
import EntitySchemaRelationSelector from '../../../entity/components/EntitySchemaRelationSelector';
import EntitySchemaSelector from '../../../entity/components/EntitySchemaSelector';
import { useEntityContext } from '../../../entity/Entity.context';
import { getEntityProperties } from '../../../entity/Entity.hooks';
import { EntitySchema } from '../../../entity/types';
import TaskTypeSelector from '../../../task-types/components/TaskTypeSelector';
import { useGetTaskTypes } from '../../../task-types/TaskType.hooks';
import { TaskType } from '../../../task-types/TaskType.interface';
import { parseTaskStatus } from '../../../tasks/task.helper';
import { useTaskRedirect } from '../../../tasks/Task.hooks';
import { Task } from '../../../tasks/Task.interface';
import {
  TaskContext,
  UpdateRelatedEntityStatusParams
} from '../../../tasks/Task.machine';
import { useWorkflowContext } from '../../Workflow.context';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';

type Relation = UpdateRelatedEntityStatusParams['data']['relations'][number];
type RelationEvent = Relation['events'][number];

function validateRelation(value: Partial<Relation>): value is Relation {
  return !!(
    value.taskType &&
    Array.isArray(value.propertyNames) &&
    value.propertyNames.length > 0 &&
    value.propertyNames.every(propertyName => !!propertyName) &&
    Array.isArray(value.events) &&
    value.events.length > 0
  );
}

const UpdateRelatedEntityStatusConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { targetEntity, onChange } = props;
  const [open, setOpen] = useState(false);
  const [invoke, setInvoke] = useState(props.invoke);
  const [updateIndex, setUpdateIndex] = useState(-1);
  const [removeIndex, setRemoveIndex] = useState(-1);
  const { schemas } = useEntityContext();
  if (!invoke.data) invoke.data = {};
  const data: UpdateRelatedEntityStatusParams['data'] = invoke.data;
  const relations = data.relations || [];
  // entity schema of current Workflow
  const schema = targetEntity
    ? schemas.find(schema => schema.name === targetEntity)
    : undefined;

  const update = (data: UpdateRelatedEntityStatusParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  const getPropertyDisplay = (relation: Relation | undefined) => {
    if (!relation) {
      return '?';
    }
    const properties = getEntityProperties(
      relation.propertyNames,
      schema,
      schemas
    );
    const property = properties
      .map(property => {
        return property.relation?.displayName || property.propertyName || '?';
      })
      .join(' -> ');
    const entity = schema?.displayName || '?';
    return `${entity} -> ${property}`;
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Related Entity</TableCell>
              <TableCell>Transitions</TableCell>
              <TableCell style={{ width: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {relations.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No relations added.</TableCell>
              </TableRow>
            )}
            {relations.map((relation, index) => (
              <TableRow key={index} className='dense'>
                <WrappedTableCell>
                  {relation.name || (
                    <Typography variant='body2' color='textSecondary'>
                      Untitled
                    </Typography>
                  )}
                </WrappedTableCell>
                <WrappedTableCell>
                  {getPropertyDisplay(relation)}
                </WrappedTableCell>
                <WrappedTableCell>
                  {relation.events
                    .map(event => {
                      return `${event.state} -> ${parseTaskStatus(
                        event.target
                      )}`;
                    })
                    .join(', ')}
                </WrappedTableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    size='small'
                    onClick={() => setUpdateIndex(index)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => setRemoveIndex(index)}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Relation
        </Button>
      </Box>

      {schema && (
        <AddRelationDialog
          schema={schema}
          open={open || updateIndex > -1}
          relation={relations[updateIndex]}
          title={updateIndex > -1 ? 'Update Relation' : 'Add Relation'}
          onCancel={() => {
            setOpen(false);
            setUpdateIndex(-1);
          }}
          onSubmit={relation => {
            data.entityName = schema.name;
            if (!data.relations) {
              data.relations = [];
            }
            if (updateIndex > -1) {
              data.relations.splice(updateIndex, 1, relation);
            } else {
              data.relations.push(relation);
            }
            setOpen(false);
            setUpdateIndex(-1);
            update({ ...data });
          }}
        />
      )}

      <ConfirmDialog
        confirmLabel='Remove'
        open={removeIndex > -1}
        title='Remove Relation'
        message={`Are you sure you want to remove ${
          relations[removeIndex]?.name || 'this'
        } relation?`}
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={() => {
          if (!data.relations) {
            data.relations = [];
          }
          if (removeIndex > -1) {
            data.relations.splice(removeIndex, 1);
          }
          setRemoveIndex(-1);
          update({ ...data });
        }}
      />
    </>
  );
};

const StyledBox = styled(Box)`
  .grid-field {
    flex: 1;
  }
  .grid-action {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;

interface AddRelationDialogProps {
  open: boolean;
  title: string;
  schema: EntitySchema;
  relation?: Relation;
  onCancel?: () => void;
  onSubmit?: (relation: Relation) => void;
}

const AddRelationDialog: React.FC<AddRelationDialogProps> = props => {
  const {
    open,
    title,
    schema,
    relation: relationProp,
    onCancel,
    onSubmit
  } = props;
  const [relation, setRelation] = useState<Partial<Relation>>(
    () => relationProp || {}
  );
  const { schemas } = useEntityContext();
  const { getWorkflow } = useWorkflowContext();
  const [{ data, isLoading: isFinding }, findTaskTypes] = useGetTaskTypes();

  const taskTypes = useMemo(() => data || [], [data]);

  const workflow = useMemo(() => {
    return relation.taskType ? getWorkflow(relation.taskType) : undefined;
  }, [relation.taskType, getWorkflow]);

  const events = useMemo(() => {
    const allEvents: RelationEvent[] = [];
    for (const state of workflow?.stateNode.states || []) {
      for (const event of state.events || []) {
        for (const transition of event.transitions || []) {
          allEvents.push({
            event: event.name || '',
            state: state.name,
            target: transition.target
          });
        }
      }
    }
    return allEvents;
  }, [workflow]);

  const selectedEvents = useMemo(() => {
    const relationEvents = relation.events || [];
    return events.filter(event => {
      return relationEvents.some(relationEvent => {
        return (
          relationEvent.state === event.state &&
          relationEvent.event === event.event &&
          relationEvent.target === event.target
        );
      });
    });
  }, [events, relation]);

  const properties = useMemo(() => {
    return getEntityProperties(relation.propertyNames, schema, schemas);
  }, [relation, schema, schemas]);

  // get last property.relation
  const propertyRelation = useMemo(() => {
    return properties[properties.length - 1]?.relation;
  }, [properties]);

  const targetEntity = propertyRelation?.target;
  const validRelation = validateRelation(relation) ? relation : undefined;

  useEffect(() => {
    targetEntity && findTaskTypes({ targetEntity });
  }, [targetEntity, findTaskTypes]);

  // clear state
  useEffect(() => {
    if (!open) {
      setRelation({});
    }
  }, [open]);

  useEffect(() => {
    relationProp && setRelation(relationProp);
  }, [relationProp]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            fullWidth
            variant='outlined'
            label='Display Name'
            placeholder='Enter the display name of the Relation'
            value={relation.name || ''}
            onChange={event => {
              setRelation(relation => {
                return { ...relation, name: event.target.value };
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <EntitySchemaSelector
            disabled
            value={schema.name}
            textFieldProps={{ label: 'Workflow Entity' }}
          />
        </Box>
        {properties.map((property, index) => (
          <StyledBox mt={2} key={`${property.propertyName}-${index}`}>
            <Grid container spacing={0}>
              <Grid item className='grid-field'>
                <EntitySchemaRelationSelector
                  schema={property.schema}
                  value={property.propertyName}
                  textFieldProps={{
                    label: index === 0 ? 'Related Entity' : 'Entity Relation',
                    placeholder: `Select a Relationship from ${
                      property.schema?.displayName || '?'
                    }`
                  }}
                  onChange={relation => {
                    const propertyName = relation?.propertyName;
                    setRelation(relation => {
                      relation = { ...relation };
                      if (!relation.propertyNames) {
                        relation.propertyNames = [];
                      }
                      relation.propertyNames[index] = propertyName || '';
                      if (!propertyName) {
                        relation.events = [];
                        relation.taskType = undefined;
                        // unset all subsequent relations
                        relation.propertyNames.splice(index + 1);
                      }
                      return relation;
                    });
                  }}
                />
              </Grid>
              {index > 0 && (
                <Grid item className='grid-action'>
                  <IconButton
                    onClick={() => {
                      setRelation(relation => {
                        relation = {
                          ...relation,
                          events: [],
                          taskType: undefined
                        };
                        relation.propertyNames?.splice(index);
                        return relation;
                      });
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </StyledBox>
        ))}
        <Box mt={2}>
          <Button
            fullWidth
            variant='outlined'
            startIcon={<AddIcon />}
            // disabled if no selected for last property
            disabled={!properties[properties.length - 1].propertyName}
            onClick={() => {
              setRelation(relation => {
                relation = {
                  ...relation,
                  events: [],
                  taskType: undefined
                };
                if (!relation.propertyNames) {
                  relation.propertyNames = [];
                }
                relation.propertyNames.push('');
                return relation;
              });
            }}
          >
            Add Relation
          </Button>
        </Box>
        <Box mt={2}>
          <TaskTypeSelector
            options={taskTypes}
            isLoading={isFinding}
            value={relation.taskType}
            disabled={!propertyRelation}
            textFieldProps={{
              label: 'Entity Task Type',
              placeholder: 'Select the Task Type of Entity'
            }}
            onChange={(taskType: TaskType | null) => {
              // add display name if not set
              const taskTypeLabel = taskType
                ? `${taskType.label} (${taskType.prefix})`
                : '';
              setRelation(relation => {
                relation = {
                  ...relation,
                  taskType: taskType?.name,
                  name: relation.name || taskTypeLabel
                };
                if (!relation.taskType) {
                  relation.events = [];
                }
                return relation;
              });
            }}
          />
        </Box>
        <Box mt={2}>
          <Autocomplete
            multiple
            options={events}
            value={selectedEvents}
            disabled={!workflow || !relation.taskType}
            getOptionLabel={option => {
              const { event, state, target } = option;
              return `${event} (${state} -> ${parseTaskStatus(target)})`;
            }}
            onChange={(_, events) => {
              setRelation(relation => ({ ...relation, events }));
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant='outlined'
                label='Event Transitions'
                placeholder='Select Event Transitions'
              />
            )}
          />
        </Box>
        {workflow && (
          <Box mt={2}>
            <Alert severity='info'>
              The <strong>event transitions</strong> will trigger for the
              related entities. The related entities must be in a status that
              can transition to the target status.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!validRelation}
          onClick={() => {
            if (validRelation) {
              onSubmit?.(validRelation);
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
`;

const UpdateRelatedEntityStatusDisplay: React.FC<
  ServiceDisplayProps
> = props => {
  const { invoke } = props;
  if (!invoke.data) invoke.data = {};
  const data: UpdateRelatedEntityStatusParams['data'] = invoke.data;
  const relations = data.relations || [];
  return (
    <StyledDisplayDiv>
      {relations.map((relation, index) => {
        const name = relation.name || 'Untitled';
        const { length } = relation.events;
        return (
          <div key={`${name}-${index}`}>
            {name}: {length} transition{length !== 1 && 's'}
          </div>
        );
      })}
    </StyledDisplayDiv>
  );
};

const StyledDialogContent = styled(DialogContent)`
  .group-title {
    padding-bottom: 0;
    margin-top: 8px;
  }
  .item {
    padding-bottom: 0;
  }
  .content {
    flex: 1;
    .title {
      margin-bottom: 1px;
      .flex {
        display: inline-flex;
        align-items: center;
        .MuiTypography-root {
          font-weight: bold;
        }
      }
    }
    .status-label {
      padding: 3px 7px;
      margin-left: 6px;
      margin-right: 6px;
    }
    .subtitle {
      margin-bottom: 2px;
    }
  }
  .icon {
    margin-top: 2px;
  }
  .error {
    color: ${props => props.theme.palette.error.main};
  }
`;

export interface UpdateRelatedEntityStatusErrorItem {
  task: Task;
  message: string;
  event: RelationEvent;
  relationName: string;
}

export interface UpdateRelatedEntityStatusError {
  type: 'updateRelatedEntityStatus';
  data: { items: UpdateRelatedEntityStatusErrorItem[] };
}

export const ErrorPluginView: React.FC<
  ErrorMetadataPluginProps<UpdateRelatedEntityStatusError>
> = props => {
  const { error, onCancel } = props;
  const { items } = error.metadata.data;
  const [selectedTask, setSelectedTask] = useState<Task>();
  const { path, redirect, result, getTaskEntity } =
    useTaskRedirect(selectedTask);
  const { isLoading } = result;

  // group items by relationName
  const groups = useMemo(() => {
    const groups: {
      relationName: string;
      items: UpdateRelatedEntityStatusErrorItem[];
    }[] = [];
    for (const item of items) {
      const { relationName } = item;
      const index = groups.findIndex(
        group => group.relationName === relationName
      );
      const group = groups[index] || { items: [], relationName };
      if (index < 0) {
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  }, [items]);

  useEffect(() => {
    if (!selectedTask) {
      return;
    }
    const value = path || redirect;
    if (value) {
      window.open(value, '_blank');
    } else {
      getTaskEntity();
    }
  }, [path, redirect, selectedTask, getTaskEntity]);

  return (
    <Dialog open onClose={onCancel}>
      <DialogTitle>Transition Error</DialogTitle>
      <StyledDialogContent>
        <Box mb={2}>
          <Typography>{error.message}</Typography>
          <Typography variant='body2' color='textSecondary'>
            Source: <strong>{UpdateRelatedEntityStatusPlugin.name}</strong>{' '}
            Plugin
          </Typography>
        </Box>
        {items.length > 0 && (
          <Box mb={1}>
            <Alert severity='warning'>
              <AlertTitle>Total Issues: {items.length}</AlertTitle>
              There are issues found while transitioning this item. You can go
              through each <strong>Related Entity</strong> below in order to
              resolve the issue and try again.
            </Alert>
          </Box>
        )}
        <Grid container spacing={2}>
          {items.length === 0 && (
            <Grid item xs={12}>
              No items to display.
            </Grid>
          )}
          {groups.map((group, groupIndex) => (
            <Fragment key={`${group.relationName}-${groupIndex}`}>
              <Grid item xs={12} className='group-title'>
                <Typography variant='body2' color='textSecondary'>
                  {group.relationName}
                </Typography>
              </Grid>
              {group.items.map((item, itemIndex) => (
                <ErrorItem
                  item={item}
                  isLoading={isLoading}
                  key={`${item.task.id}-${itemIndex}`}
                  onClick={() => setSelectedTask(item.task)}
                />
              ))}
            </Fragment>
          ))}
        </Grid>
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ErrorItemProps {
  item: UpdateRelatedEntityStatusErrorItem;
  isLoading?: boolean;
  onClick?: () => void;
}

const ErrorItem: React.FC<ErrorItemProps> = props => {
  const { item, isLoading, onClick } = props;
  const { task, event } = item;
  return (
    <Grid item xs={12} className='item'>
      <Grid container spacing={2}>
        <Grid item>
          <HighlightOffIcon className='icon error' />
        </Grid>
        <Grid item className='content'>
          <div className='title'>
            <Box className='flex'>
              <Typography variant='subtitle1'>{task.key}</Typography>
              <TaskStatusLabel task={task} />
              <Tooltip arrow placement='right' title='Open in New Tab'>
                <IconButton
                  size='small'
                  color='primary'
                  disabled={isLoading}
                  onClick={onClick}
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
          <Typography variant='subtitle2'>{task.name}</Typography>
          <Typography
            variant='body2'
            color='textSecondary'
            className='subtitle'
          >
            {event.event} ({event.state}
            {' -> '}
            {parseTaskStatus(event.target)})
          </Typography>
          <Typography color={!item.message ? 'textSecondary' : undefined}>
            {item.message || 'No error message.'}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const UpdateRelatedEntityStatusErrorPlugin: ErrorMetadataPlugin<UpdateRelatedEntityStatusError> =
  {
    type: 'updateRelatedEntityStatus',
    render: ErrorPluginView
  };

export const UpdateRelatedEntityStatusPlugin: ServicePlugin<UpdateRelatedEntityStatusParams> =
  {
    src: 'updateRelatedEntityStatus',
    name: 'Update Related Entity Status',
    configurator: UpdateRelatedEntityStatusConfigurator,
    display: UpdateRelatedEntityStatusDisplay,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data = condition.data as UpdateRelatedEntityStatusParams['data'];
      return !!(
        data &&
        Array.isArray(data.relations) &&
        data.relations.length > 0 &&
        data.relations.every(relation => {
          return (
            relation.taskType &&
            Array.isArray(relation.events) &&
            relation.events.length > 0 &&
            relation.events.every(
              event => event.event && event.state && event.target
            ) &&
            Array.isArray(relation.propertyNames) &&
            relation.propertyNames.length > 0 &&
            relation.propertyNames.every(name => !!name)
          );
        })
      );
    },
    service: (
      state: State<TaskContext>,
      params:
        | UpdateRelatedEntityStatusParams
        | UpdateRelatedEntityStatusParams[]
    ) => {
      console.log('updateRelatedEntityStatus params', state, params);
      return true;
    }
  };
